/**
 * Admin API Service
 * Handles API calls for admin dashboard CRUD operations
 */

import authService from './authService';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'http://localhost:8000';

const getAuthHeaders = async () => {
  const token = await authService.getToken();
  return {
    'Authorization': `Bearer ${token}`,
    'Content-Type': 'application/json'
  };
};

class AdminAPI {
  /**
   * Get database schema for all admin tables
   */
  static async getSchema() {
    try {
      const headers = await getAuthHeaders();
      const response = await fetch(`${API_BASE_URL}/api/v1/admin/schema`, { headers });
      if (!response.ok) {
        const error = await response.json().catch(() => ({ detail: response.statusText }));
        throw new Error(error.detail || 'Failed to fetch schema');
      }
      return response.json();
    } catch (error) {
      console.error('AdminAPI.getSchema error:', error);
      throw error;
    }
  }

  /**
   * List all rows from a table
   */
  static async listRows(tableName) {
    try {
      const headers = await getAuthHeaders();
      const response = await fetch(`${API_BASE_URL}/api/v1/admin/tables/${tableName}`, { headers });
      if (!response.ok) {
        const error = await response.json().catch(() => ({ detail: response.statusText }));
        throw new Error(error.detail || `Failed to fetch rows from ${tableName}`);
      }
      return response.json();
    } catch (error) {
      console.error(`AdminAPI.listRows(${tableName}) error:`, error);
      throw error;
    }
  }

  /**
   * Get a single row by ID
   */
  static async getRow(tableName, rowId) {
    try {
      const headers = await getAuthHeaders();
      const response = await fetch(`${API_BASE_URL}/api/v1/admin/tables/${tableName}/${rowId}`, { headers });
      if (!response.ok) {
        const error = await response.json().catch(() => ({ detail: response.statusText }));
        throw new Error(error.detail || `Failed to fetch row ${rowId}`);
      }
      return response.json();
    } catch (error) {
      console.error(`AdminAPI.getRow(${tableName}, ${rowId}) error:`, error);
      throw error;
    }
  }

  /**
   * Create a new row
   */
  static async createRow(tableName, data) {
    try {
      const headers = await getAuthHeaders();
      const response = await fetch(`${API_BASE_URL}/api/v1/admin/tables/${tableName}`, {
        method: 'POST',
        headers,
        body: JSON.stringify({ data })
      });
      if (!response.ok) {
        const error = await response.json().catch(() => ({ detail: response.statusText }));
        throw new Error(error.detail || 'Failed to create row');
      }
      return response.json();
    } catch (error) {
      console.error(`AdminAPI.createRow(${tableName}) error:`, error);
      throw error;
    }
  }

  /**
   * Update an existing row
   */
  static async updateRow(tableName, rowId, data) {
    try {
      const headers = await getAuthHeaders();
      const response = await fetch(`${API_BASE_URL}/api/v1/admin/tables/${tableName}/${rowId}`, {
        method: 'PUT',
        headers,
        body: JSON.stringify({ data })
      });
      if (!response.ok) {
        const error = await response.json().catch(() => ({ detail: response.statusText }));
        throw new Error(error.detail || 'Failed to update row');
      }
      return response.json();
    } catch (error) {
      console.error(`AdminAPI.updateRow(${tableName}, ${rowId}) error:`, error);
      throw error;
    }
  }

  /**
   * List all stored routines
   */
  static async listRoutines() {
    try {
      const headers = await getAuthHeaders();
      const response = await fetch(`${API_BASE_URL}/api/v1/admin/routines`, { headers });
      if (!response.ok) {
        const error = await response.json().catch(() => ({ detail: response.statusText }));
        throw new Error(error.detail || 'Failed to fetch routines');
      }
      return response.json();
    } catch (error) {
      console.error('AdminAPI.listRoutines error:', error);
      throw error;
    }
  }

  /**
   * Get a single routine's full definition
   */
  static async getRoutine(oid) {
    try {
      const headers = await getAuthHeaders();
      const response = await fetch(`${API_BASE_URL}/api/v1/admin/routines/${oid}`, { headers });
      if (!response.ok) {
        const error = await response.json().catch(() => ({ detail: response.statusText }));
        throw new Error(error.detail || `Failed to fetch routine ${oid}`);
      }
      return response.json();
    } catch (error) {
      console.error(`AdminAPI.getRoutine(${oid}) error:`, error);
      throw error;
    }
  }

  /**
   * Update a routine's definition
   */
  static async updateRoutine(oid, definition) {
    try {
      const headers = await getAuthHeaders();
      const response = await fetch(`${API_BASE_URL}/api/v1/admin/routines/${oid}`, {
        method: 'PUT',
        headers,
        body: JSON.stringify({ definition })
      });
      if (!response.ok) {
        const error = await response.json().catch(() => ({ detail: response.statusText }));
        throw new Error(error.detail || 'Failed to update routine');
      }
      return response.json();
    } catch (error) {
      console.error(`AdminAPI.updateRoutine(${oid}) error:`, error);
      throw error;
    }
  }

  /**
   * Delete a row
   */
  static async deleteRow(tableName, rowId) {
    try {
      const headers = await getAuthHeaders();
      const response = await fetch(`${API_BASE_URL}/api/v1/admin/tables/${tableName}/${rowId}`, {
        method: 'DELETE',
        headers
      });
      if (!response.ok) {
        const error = await response.json().catch(() => ({ detail: response.statusText }));
        throw new Error(error.detail || 'Failed to delete row');
      }
      return response.json();
    } catch (error) {
      console.error(`AdminAPI.deleteRow(${tableName}, ${rowId}) error:`, error);
      throw error;
    }
  }
}

export default AdminAPI;
